import type { LoginResponse, LoginUser } from '../types/api'

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object'
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string')
}

function isPermissionsMap(value: unknown): value is Record<string, string[]> {
  if (!isRecord(value)) return false
  return Object.values(value).every((actions) => isStringArray(actions))
}

function withPermissions(user: Record<string, unknown>, permissions: unknown): LoginUser {
  if (isStringArray(permissions) || isPermissionsMap(permissions)) {
    return { ...user, permissions } as unknown as LoginUser
  }

  return user as unknown as LoginUser
}

/**
 * Inner login payload after axios unwraps `{ success, data }` (legacy nested `data` still supported).
 */
export function parseLoginResponse(raw: unknown): LoginResponse | null {
  if (!isRecord(raw)) return null

  const payload = isRecord(raw.data) ? raw.data : raw

  const { accessToken, refreshToken, user } = payload

  if (typeof accessToken !== 'string' || typeof refreshToken !== 'string' || !isRecord(user)) {
    return null
  }

  return {
    success: typeof raw.success === 'boolean' ? raw.success : true,
    accessToken,
    refreshToken,
    user: user as unknown as LoginUser,
  }
}

/** Inner `/me` payload: `{ user }` after unwrap (legacy nested `data.user` supported). */
export function parseMeResponse(raw: unknown): LoginUser | null {
  if (!isRecord(raw)) return null

  const direct = raw.user
  if (isRecord(direct)) {
    return withPermissions(direct, raw.permissions)
  }

  const nested = raw.data
  if (isRecord(nested) && isRecord(nested.user)) {
    return withPermissions(nested.user, nested.permissions)
  }

  return null
}

/** Verify-OTP inner `{ token }` (legacy nested supported). */
export function parseVerifyOtpToken(raw: unknown): string | null {
  if (!isRecord(raw)) return null

  const direct = raw.token
  if (typeof direct === 'string' && direct.length > 0) {
    return direct
  }

  const nested = raw.data
  if (isRecord(nested) && typeof nested.token === 'string' && nested.token.length > 0) {
    return nested.token
  }

  return null
}
